import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';

type DiffResult = RouterOutputs['shadowTimesheets']['diff'];
type DiffRow = DiffResult['byDate'][number];

interface ShadowDiffPanelProps {
  userId: string;
  projectId: string;
  periodFrom: string;
  periodTo: string;
}

const STATUS_STYLES: Record<DiffRow['status'], { row: string; badge: string; label: string }> = {
  added:     { row: 'bg-green-50 dark:bg-green-950/20',  badge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',  label: 'Added'   },
  removed:   { row: 'bg-red-50 dark:bg-red-950/20',     badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',          label: 'Removed' },
  modified:  { row: 'bg-amber-50 dark:bg-amber-950/20', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',   label: 'Changed' },
  unchanged: { row: '',                                  badge: 'bg-muted text-muted-foreground',                                        label: 'Same'    },
};

function fmtDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function fmtHours(h: number | null): string {
  if (h === null) return '—';
  return `${h.toFixed(1)}h`;
}

function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) return <span className="text-sm text-muted-foreground">no change</span>;
  const color = delta > 0 ? 'text-good' : 'text-destructive';
  return (
    <span className={`text-sm font-semibold tabular-nums ${color}`}>
      {delta > 0 ? '+' : ''}{delta.toFixed(1)}h
    </span>
  );
}

export function ShadowDiffPanel({ userId, projectId, periodFrom, periodTo }: ShadowDiffPanelProps) {
  const enabled = !!userId && !!projectId;

  const { data: diff, isLoading } = trpc.shadowTimesheets.diff.useQuery(
    { user_id: userId, project_id: projectId, period_from: periodFrom, period_to: periodTo },
    { enabled },
  );

  if (!userId || !projectId) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">Select an employee and project.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!diff?.hasShadow) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">No shadow exists for this period.</p>
      </div>
    );
  }

  const changedRows = diff.byDate.filter((r) => r.status !== 'unchanged');
  const unchangedRows = diff.byDate.filter((r) => r.status === 'unchanged');

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Summary card */}
      <div className="border-b px-6 py-4 shrink-0">
        <div className="flex items-center gap-8">
          <div>
            <p className="text-xs text-muted-foreground">Real</p>
            <p className="tabular-nums text-xl font-semibold">{diff.real_hours.toFixed(1)}h</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Shadow</p>
            <p className="tabular-nums text-xl font-semibold">{diff.shadow_hours.toFixed(1)}h</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Delta</p>
            <DeltaBadge delta={diff.delta} />
          </div>
          <div className="ml-auto flex gap-2 text-xs text-muted-foreground">
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-700">
              {diff.byDate.filter((r) => r.status === 'added').length} added
            </span>
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-red-700">
              {diff.byDate.filter((r) => r.status === 'removed').length} removed
            </span>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">
              {diff.byDate.filter((r) => r.status === 'modified').length} changed
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Column headers */}
        <div className="grid grid-cols-[56px_1fr_56px_1fr_56px_72px] gap-2 border-b px-6 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div>Date</div>
          <div>Real description</div>
          <div className="text-right">Real h</div>
          <div>Shadow description</div>
          <div className="text-right">Shadow h</div>
          <div className="text-center">Status</div>
        </div>

        {diff.byDate.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No entries in this period.
          </div>
        )}

        {/* Changed rows */}
        {changedRows.map((row) => {
          const s = STATUS_STYLES[row.status];
          return (
            <div
              key={row.date}
              className={`grid grid-cols-[56px_1fr_56px_1fr_56px_72px] gap-2 items-center border-b px-6 py-2.5 text-xs ${s.row}`}
            >
              <div className="tabular-nums text-muted-foreground">{fmtDate(row.date)}</div>
              <div className={`truncate ${row.status === 'removed' ? 'line-through text-muted-foreground' : ''}`}>
                {row.real_description ?? '—'}
              </div>
              <div className="tabular-nums text-right">{fmtHours(row.real_hours)}</div>
              <div className={`truncate ${row.status === 'added' ? 'font-medium' : ''}`}>
                {row.shadow_description ?? '—'}
              </div>
              <div className={`tabular-nums text-right ${row.status === 'added' ? 'font-medium' : ''}`}>
                {fmtHours(row.shadow_hours)}
              </div>
              <div className="flex justify-center">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${s.badge}`}>
                  {s.label}
                </span>
              </div>
            </div>
          );
        })}

        {/* Unchanged rows (collapsed summary) */}
        {unchangedRows.length > 0 && (
          <div className="px-6 py-3 text-xs text-muted-foreground">
            {unchangedRows.length} unchanged entr{unchangedRows.length === 1 ? 'y' : 'ies'} not shown.
          </div>
        )}
      </div>
    </div>
  );
}
